import { getRelativeTime } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import i18n from '@/plugins/i18n';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

interface AuthorSignatureParams {
    /** Full name of the author, or null when nobody can tell who they are. */
    authorName: string | null;
    authorPictureUrl?: string | null;
    isAnonymousRespondent?: boolean;
    /** Omitted where the date is shown apart from the signature, or where nothing may date it. */
    writtenAt?: Date;
    /**
     * Stands for a name not known yet, such as the respondent's own in a preview: without it, an unknown
     * author is one whose account was deleted.
     */
    nameWhenUnknown?: string;
}

export interface AuthorSignature {
    name: string;
    /** Absent when the author is unknown or anonymous: the avatar falls back to a neutral silhouette. */
    initials?: string;
    pictureUrl?: string;
    /** Absent for an anonymous respondent: when they answered could tell who they are. */
    writtenAgo?: string;
}

/**
 * How an answer, a comment or a reply is signed: in the results, in the respondent's thread, and in the
 * preview the respondent is shown before answering.
 */
export const useAuthorSignature = ({
    authorName,
    authorPictureUrl,
    isAnonymousRespondent = false,
    writtenAt,
    nameWhenUnknown,
}: AuthorSignatureParams): AuthorSignature => {
    const { t } = useAmelioTranslations();

    if (isAnonymousRespondent) {
        return { name: t('answer.feedbackPrivacy.respondent') };
    }

    const writtenAgo = writtenAt && getRelativeTime({ date: writtenAt, language: i18n.language });

    if (!authorName) {
        return { name: nameWhenUnknown ?? t('answer.feedbackPrivacy.deletedAccount'), writtenAgo };
    }

    return {
        name: authorName,
        initials: getInitials(authorName),
        pictureUrl: getProfilePictureUrl(authorPictureUrl) || undefined,
        writtenAgo,
    };
};
